import { Badge, Button } from "@izicvs/ui";
import { t } from "@lingui/macro";
import { ArrowSquareOut, CircleNotch, Crown, Sparkle } from "@phosphor-icons/react";

import { useAiUsage } from "@/client/services/ai/usage";
import { useBillingPortal, useCheckout, useSubscription } from "@/client/services/billing";

export const SubscriptionSettings = () => {
  const { subscription, loading } = useSubscription();
  const { usage } = useAiUsage();
  const { checkout, loading: checkingOut } = useCheckout();
  const { openPortal, loading: openingPortal } = useBillingPortal();

  const plan = subscription?.plan ?? "free";
  const billingEnabled = Boolean(subscription?.billingEnabled);
  const viaOrganization = Boolean(usage?.viaOrganization);
  const status = subscription?.subscriptionStatus;
  const isPaid = plan === "pro" || plan === "autopilot";

  let planLabel = t`Free`;
  if (plan === "pro") planLabel = t`Pro`;
  else if (plan === "autopilot") planLabel = t`Autopilot`;

  const renewDate = subscription?.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString()
    : "";
  const passDate = subscription?.templatesPassEnd
    ? new Date(subscription.templatesPassEnd).toLocaleDateString()
    : "";

  return (
    <div className="space-y-6">
      <div>
        <h3 className="flex items-center gap-x-2 text-2xl font-bold leading-relaxed tracking-tight">
          <Crown />
          {t`Subscription`}
        </h3>
        <p className="leading-relaxed opacity-75">
          {t`Manage your plan, payment method and invoices.`}
        </p>
      </div>

      {loading && (
        <div className="rounded-lg border p-4 text-sm opacity-60">{t`Loading...`}</div>
      )}

      {!loading && !billingEnabled && (
        <div className="rounded-lg border p-4 text-sm opacity-75">
          {t`Billing is not enabled on this instance, so all features are available.`}
        </div>
      )}

      {!loading && billingEnabled && (
        <div className="rounded-lg border p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-2 font-medium">
                {t`Current plan`}
                <Badge variant={isPaid ? "primary" : "secondary"}>{planLabel}</Badge>
                {status === "trialing" && <Badge variant="info">{t`Trial`}</Badge>}
                {status === "past_due" && <Badge variant="error">{t`Payment due`}</Badge>}
              </p>
              {viaOrganization && (
                <p className="mt-1 text-xs opacity-60">{t`Provided by your organization.`}</p>
              )}
              {!viaOrganization && isPaid && renewDate && (
                <p className="mt-1 text-xs opacity-60">{t`Next renewal on ${renewDate}.`}</p>
              )}
            </div>

            <div className="flex shrink-0 flex-wrap gap-2">
              {!viaOrganization && isPaid && (
                <Button
                  variant="outline"
                  disabled={openingPortal}
                  onClick={() => void openPortal()}
                >
                  {openingPortal ? (
                    <CircleNotch className="mr-2 animate-spin" />
                  ) : (
                    <ArrowSquareOut className="mr-2" />
                  )}
                  {t`Manage subscription`}
                </Button>
              )}
              {!viaOrganization && !isPaid && (
                <>
                  <Button disabled={checkingOut} onClick={() => void checkout("pro")}>
                    <Crown weight="fill" className="mr-2" />
                    {t`Upgrade to Pro`}
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={checkingOut}
                    onClick={() => void checkout("autopilot")}
                  >
                    <Sparkle weight="fill" className="mr-2" />
                    {t`Autopilot`}
                  </Button>
                </>
              )}
            </div>
          </div>

          {subscription?.templatesAccess && passDate && (
            <p className="mt-3 border-t pt-3 text-xs opacity-60">
              {t`Premium templates pass active until ${passDate}.`}
            </p>
          )}

          {!viaOrganization && isPaid && (
            <p className="mt-3 text-xs opacity-50">
              {t`Cancel anytime from the portal; you keep access until the end of the paid period.`}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
